import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, CloudUpload, Link as LinkIcon, Loader2, Trash2, UploadCloud, XCircle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RevisionCompare } from "@/components/viewer/RevisionCompare";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";

/** Chunked resumable upload of large .3dm models + import report. */
export function ModelUploadDialog({ projectId, open, onOpenChange, onActivated, onShowInViewer }) {
  const [revisionName, setRevisionName] = useState("");
  const [file, setFile] = useState(null);
  const [sourceUrl, setSourceUrl] = useState("");
  const [mode, setMode] = useState("file");
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState("idle");
  const [revision, setRevision] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [view, setView] = useState("upload");
  const pollRef = useRef(null);

  const loadRevisions = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/models`);
      setRevisions(data);
      if (!revisionName) setRevisionName(`R${String(data.length + 1).padStart(2, "0")}`);
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  useEffect(() => {
    if (open) loadRevisions();
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const watchProcessing = (revisionId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const { data } = await api.get(`/projects/${projectId}/models/${revisionId}`);
      setRevision(data);
      if (data.processing_status === "processed" || data.processing_status === "failed") {
        clearInterval(pollRef.current);
        setPhase(data.processing_status === "processed" ? "report" : "failed");
        loadRevisions();
      }
    }, 2500);
  };

  const startFromLink = async () => {
    setPhase("processing");
    try {
      const { data } = await api.post(`/projects/${projectId}/models/import-url`, {
        url: sourceUrl.trim(),
        revision_name: revisionName,
      });
      setRevision(data.revision);
      toast.success("Download started on the server");
      watchProcessing(data.revision.id);
    } catch (error) {
      toast.error(apiError(error));
      setPhase("idle");
    }
  };

  const start = async () => {
    if (!file) return;
    setPhase("uploading");
    setProgress(0);
    try {
      const init = await api.post(`/projects/${projectId}/models/uploads`, {
        file_name: file.name,
        file_size: file.size,
        revision_name: revisionName,
      });
      const revisionId = init.data.revision.id;
      const chunkSize = init.data.chunk_size;
      let offset = 0;
      while (offset < file.size) {
        const slice = file.slice(offset, offset + chunkSize);
        // eslint-disable-next-line no-await-in-loop
        const res = await api.put(
          `/projects/${projectId}/models/uploads/${revisionId}/chunk?offset=${offset}`,
          slice,
          { headers: { "Content-Type": "application/octet-stream" } }
        );
        offset = res.data.resume_offset;
        setProgress(Math.round((offset / file.size) * 100));
      }
      await api.post(`/projects/${projectId}/models/uploads/${revisionId}/complete`);
      setPhase("processing");
      watchProcessing(revisionId);
    } catch (error) {
      toast.error(apiError(error));
      setPhase("idle");
    }
  };

  const activate = async (revisionId) => {
    try {
      await api.post(`/projects/${projectId}/models/${revisionId}/activate`);
      toast.success("Revision activated");
      onOpenChange(false);
      onActivated?.();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const remove = async (rev) => {
    if (!window.confirm(`Delete revision ${rev.revision_name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/projects/${projectId}/models/${rev.id}`);
      toast.success(`${rev.revision_name} deleted`);
      if (revision?.id === rev.id) setRevision(null);
      loadRevisions();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const report = revision?.import_report;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="model-upload-dialog"
        className="max-h-[92vh] max-w-2xl overflow-y-auto border-slate-200 bg-white text-slate-900"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-lg">
            <CloudUpload size={18} className="text-cyan-600" /> Model revisions
          </DialogTitle>
          <DialogDescription className="text-[11.5px] text-slate-500">
            Upload a Rhino .3dm file or import it from a share link, then publish the revision.
          </DialogDescription>
        </DialogHeader>

        {phase === "idle" ? (
          <div className="flex gap-1.5">
            {[
              ["upload", "Upload"],
              ["compare", "Compare revisions"],
            ].map(([key, label]) => (
              <button
                key={key}
                data-testid={`revision-view-${key}`}
                onClick={() => setView(key)}
                className={`mono rounded-full px-3 py-1.5 text-[10px] uppercase tracking-wider ${
                  view === key
                    ? "bg-cyan-500/18 text-cyan-700"
                    : "border border-slate-200 text-slate-600 hover:bg-slate-100"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        ) : null}

        {phase === "idle" && view === "compare" ? (
          <RevisionCompare
            projectId={projectId}
            revisions={revisions}
            onShowInViewer={(result) => {
              onShowInViewer?.(result);
              onOpenChange(false);
            }}
          />
        ) : null}

        {phase === "idle" && view === "upload" ? (
          <div className="space-y-3">
            <div>
              <p className="mono mb-1.5 text-[10px] uppercase tracking-wider text-slate-600">
                Revision name
              </p>
              <Input
                data-testid="revision-name-input"
                value={revisionName}
                onChange={(e) => setRevisionName(e.target.value)}
                className="mono border-slate-200 bg-[#EEF2F8] text-sm"
              />
            </div>
            <div className="flex gap-1.5">
              {[
                ["file", "Local file"],
                ["link", "From link"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  data-testid={`upload-mode-${key}`}
                  onClick={() => setMode(key)}
                  className={`mono rounded-full px-3 py-1 text-[9.5px] uppercase tracking-wider ${
                    mode === key
                      ? "bg-cyan-500/18 text-cyan-700"
                      : "border border-slate-200 text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {mode === "file" ? (
              <div>
                <p className="mono mb-1.5 text-[10px] uppercase tracking-wider text-slate-600">
                  Rhino .3dm file
                </p>
                <input
                  data-testid="model-file-input"
                  type="file"
                  accept=".3dm"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="w-full rounded-md border border-slate-200 bg-[#EEF2F8] p-2.5 text-xs text-slate-700 file:mr-3 file:rounded-full file:border-0 file:bg-cyan-500/15 file:px-3 file:py-1.5 file:text-[10px] file:uppercase file:tracking-wider file:text-cyan-700"
                />
                {file ? (
                  <p className="mono mt-1.5 text-[10px] text-slate-500">
                    {(file.size / 1024 / 1024).toFixed(1)} MB · uploaded in 8 MB resumable chunks
                  </p>
                ) : null}
              </div>
            ) : (
              <div>
                <p className="mono mb-1.5 text-[10px] uppercase tracking-wider text-slate-600">
                  Yandex Disk or direct .3dm link
                </p>
                <Input
                  data-testid="model-url-input"
                  value={sourceUrl}
                  onChange={(e) => setSourceUrl(e.target.value)}
                  placeholder="https://disk.yandex.com/d/…"
                  className="border-slate-200 bg-[#EEF2F8] text-xs"
                />
                <p className="mono mt-1.5 text-[10px] text-slate-500">
                  The server downloads the file itself — no browser upload needed.
                </p>
              </div>
            )}
            {mode === "file" ? (
              <Button
                data-testid="start-upload-button"
                disabled={!file || !revisionName}
                onClick={start}
                className="h-10 w-full rounded-full bg-cyan-500 text-xs text-[#04121a] hover:bg-cyan-400"
              >
                <UploadCloud size={15} className="mr-1.5" /> Upload & process
              </Button>
            ) : (
              <Button
                data-testid="start-link-import-button"
                disabled={!sourceUrl.trim() || !revisionName}
                onClick={startFromLink}
                className="h-10 w-full rounded-full bg-cyan-500 text-xs text-[#04121a] hover:bg-cyan-400"
              >
                <LinkIcon size={15} className="mr-1.5" /> Import from link
              </Button>
            )}
          </div>
        ) : null}

        {phase === "uploading" || phase === "processing" ? (
          <div data-testid="upload-progress" className="py-4">
            <div className="flex items-center justify-between text-xs">
              <span className="mono uppercase tracking-wider text-cyan-700">
                {phase === "uploading" ? "Uploading" : "Processing model"}
              </span>
              <span className="mono text-slate-600">
                {phase === "uploading" ? (
                  `${progress}%`
                ) : (
                  <span className="flex items-center gap-2" data-testid="processing-percent">
                    {revision?.processing_percent ? `${revision.processing_percent}%` : null}
                    <Loader2 size={13} className="animate-spin" />
                  </span>
                )}
              </span>
            </div>
            {phase === "uploading" && file ? (
              <p className="mono mt-1.5 text-[10px] text-slate-500">
                {((file.size * progress) / 100 / 1024 / 1024).toFixed(1)} /{" "}
                {(file.size / 1024 / 1024).toFixed(1)} MB
              </p>
            ) : null}
            <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full bg-cyan-400 transition-[width] duration-300"
                style={{
                  width: `${
                    phase === "uploading" ? progress : revision?.processing_percent || 5
                  }%`,
                }}
              />
            </div>
            {phase === "processing" ? (
              <p className="mt-3 text-[11px] text-slate-500" data-testid="processing-stage">
                {{
                  parsing: "Reading the Rhino file and resolving nested blocks…",
                  geometry: "Building reusable instanced geometry buffers…",
                  panels: "Detecting panels and validating names…",
                  database: "Writing panel and workflow records…",
                }[revision?.processing_stage] ||
                  "Detecting panels, resolving nested blocks and generating instanced viewer assets…"}
              </p>
            ) : null}
          </div>
        ) : null}

        {phase === "failed" ? (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4" data-testid="processing-failed">
            <XCircle className="text-red-600" size={18} />
            <p className="mt-2 text-sm text-red-700">{revision?.processing_error}</p>
          </div>
        ) : null}

        {phase === "report" && report && !revision?.reused_from ? (
          <div data-testid="import-report" className="space-y-3">
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/8 p-4">
              <p className="mono flex items-center gap-2 text-[11px] uppercase tracking-wider text-emerald-700">
                <CheckCircle2 size={14} /> Model import complete
              </p>
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                  ["Panels detected", report.panels_detected],
                  ["Valid names", report.valid_panel_names],
                  ["Invalid names", report.invalid_panel_names],
                  ["Duplicates", report.duplicate_panel_names],
                  ["Block definitions", report.block_definitions],
                  ["Vertices (unique)", report.total_vertices],
                  ["Loose objects", report.loose_objects],
                  ["Process time", `${report.total_seconds}s`],
                ].map(([label, value]) => (
                  <div key={label}>
                    <p className="mono text-lg text-slate-900">{value}</p>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
                  </div>
                ))}
              </div>
            </div>

            {report.invalid_objects?.length ? (
              <div className="okn-scroll max-h-48 overflow-y-auto rounded-lg border border-amber-500/25 bg-amber-500/5 p-3">
                <p className="mono mb-2 text-[10px] uppercase tracking-wider text-amber-700">
                  Invalid objects ({report.invalid_panel_names})
                </p>
                {report.invalid_objects.map((row) => (
                  <div key={row.object_id} className="border-b border-slate-200 py-1.5 last:border-0">
                    <p className="mono text-[11px] text-slate-800">{row.name || "(unnamed)"}</p>
                    <p className="text-[10px] text-amber-700">{row.errors.join(" · ")}</p>
                  </div>
                ))}
              </div>
            ) : null}

            <Button
              data-testid="activate-revision-button"
              onClick={() => activate(revision.id)}
              className="h-10 w-full rounded-full bg-cyan-500 text-xs text-[#04121a] hover:bg-cyan-400"
            >
              Publish & set as active revision
            </Button>
          </div>
        ) : null}

        {phase === "report" && revision?.reused_from ? (
          <div data-testid="reused-assets-report" className="space-y-3">
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/8 p-4">
              <p className="mono flex items-center gap-2 text-[11px] uppercase tracking-wider text-emerald-700">
                <CheckCircle2 size={14} /> Identical model — assets reused
              </p>
              <p className="mt-2 text-[11.5px] text-slate-600">
                These bytes match
                {revision.reused_from_name ? ` revision ${revision.reused_from_name}` : " an earlier revision"},
                so the viewer package was reused instead of rebuilt. {revision.panel_count} panels
                are available.
              </p>
            </div>
            <Button
              data-testid="activate-revision-button"
              onClick={() => activate(revision.id)}
              className="h-10 w-full rounded-full bg-cyan-500 text-xs text-[#04121a] hover:bg-cyan-400"
            >
              Publish &amp; set as active revision
            </Button>
          </div>
        ) : null}

        {phase === "report" && !report && !revision?.reused_from && revision ? (
          <div data-testid="processed-no-report" className="space-y-3">
            <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/8 p-4">
              <p className="mono flex items-center gap-2 text-[11px] uppercase tracking-wider text-cyan-700">
                <CheckCircle2 size={14} /> Processed
              </p>
              <p className="mt-2 text-[11.5px] text-slate-600">
                {revision.panel_count} panels are ready in this revision.
              </p>
            </div>
            <Button
              data-testid="activate-revision-button"
              onClick={() => activate(revision.id)}
              className="h-10 w-full rounded-full bg-cyan-500 text-xs text-[#04121a] hover:bg-cyan-400"
            >
              Publish &amp; set as active revision
            </Button>
          </div>
        ) : null}

        <div className="border-t border-slate-200 pt-4">
          <p className="mono mb-2 text-[10px] uppercase tracking-wider text-slate-500">
            Revision history
          </p>
          <div className="space-y-1.5" data-testid="revision-list">
            {revisions.map((rev) => (
              <div
                key={rev.id}
                className="flex items-center gap-3 rounded-lg border border-slate-200 bg-[#EEF2F8] px-3 py-2"
              >
                <span className="mono text-xs text-cyan-700">{rev.revision_name}</span>
                <span className="mono truncate text-[10px] text-slate-500">
                  {rev.original_file_name} · {(rev.file_size / 1024 / 1024).toFixed(1)} MB
                </span>
                <span className="mono ml-auto text-[10px] text-slate-600">
                  {rev.panel_count} panels
                </span>
                {rev.is_active ? (
                  <span className="mono rounded bg-emerald-500/15 px-2 py-0.5 text-[9px] uppercase tracking-wider text-emerald-700">
                    Active
                  </span>
                ) : rev.processing_status === "processed" ? (
                  <button
                    data-testid={`activate-${rev.revision_name}`}
                    onClick={() => activate(rev.id)}
                    className="mono rounded bg-slate-100 px-2 py-0.5 text-[9px] uppercase tracking-wider text-slate-700 hover:bg-cyan-500/20 hover:text-cyan-700"
                  >
                    Activate
                  </button>
                ) : (
                  <span className="mono text-[9px] uppercase tracking-wider text-slate-500">
                    {rev.processing_status}
                  </span>
                )}
                {rev.is_active ? null : (
                  <button
                    data-testid={`delete-revision-${rev.revision_name}`}
                    onClick={() => remove(rev)}
                    title="Delete this revision"
                    className="grid h-6 w-6 place-items-center rounded text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
